import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { useAuth } from '../../contexts/AuthContext';
import {
  invalidateLearningAttention,
  invalidateLearningConfigs,
  LearningConfigSummary,
  learningPath,
} from '../../services/learningApi';
import { useApis, useLearningConfigs, useLearningStatus } from './common';

interface Props {
  /** Any saved version id or the original_uuid; empty for an unsaved function. */
  functionId?: string;
  originalUuid?: string;
}

/** Self-improvement status and on/off switch for the function edit page. */
export const FunctionLearningAccordion: React.FC<Props> = ({ functionId, originalUuid }) => {
  const navigate = useNavigate();
  const { isPlatformAdmin, isImpersonating, isPublicMode } = useAuth();
  const apis = useApis();
  const status = useLearningStatus();
  const configs = useLearningConfigs(status);
  const [config, setConfig] = useState<LearningConfigSummary | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const key = `function:${originalUuid || functionId}`;
  useEffect(() => {
    setConfig(configs[key] || null);
  }, [configs, key]);

  if (isPublicMode || !status) return null;
  if (!status.enabled && (!isPlatformAdmin || isImpersonating)) return null;

  const on = !!config?.enabled;
  const open = learningPath('function', functionId || '');

  const toggle = async (enabled: boolean) => {
    if (!functionId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apis.learning.updateTarget('function', functionId, { enabled });
      setConfig(res.config);
      invalidateLearningConfigs();
      invalidateLearningAttention();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const summary = !status.enabled
    ? 'Off for this account'
    : !functionId
      ? 'Learn from this function’s runs'
      : on
        ? [
            config?.open_suggestions ? `${config.open_suggestions} suggestion${config.open_suggestions === 1 ? '' : 's'} to review` : null,
            config?.last_cycle_at ? `last analysis ${new Date(config.last_cycle_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'no analysis yet',
          ].filter(Boolean).join(' · ')
        : 'Learn from this function’s runs';

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mt: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TipsAndUpdatesIcon fontSize="small" color={on ? 'warning' : 'action'} />
          <Typography sx={{ fontWeight: 600 }}>Self-improvement</Typography>
          {status.enabled && functionId && (
            <Chip size="small" label={on ? 'On' : 'Off'} color={on ? 'warning' : 'default'} variant="outlined" />
          )}
          <Typography variant="body2" color="text.secondary">{summary}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {!status.enabled ? (
          <Alert
            severity="info"
            variant="outlined"
            action={<Button size="small" onClick={() => navigate('/dashboard/admin/learning')}>Open Admin</Button>}
          >
            Self-improvement is off for this account. Turn it on in Admin to let functions learn from their runs.
          </Alert>
        ) : !functionId ? (
          <Alert severity="info" variant="outlined">
            Save the function first. Self-improvement learns from a saved function’s runs and carries over to every new version.
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Analyzes this function’s past runs, builds a knowledge base of what works, gives relevant snippets to new runs,
              and suggests instruction changes for you to review. Nothing changes in the function without your approval.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <FormControlLabel
                control={<Switch checked={on} disabled={saving} onChange={(e) => toggle(e.target.checked)} />}
                label={on ? 'Learning from runs' : 'Learn from this function’s runs'}
              />
              {config && (
                <>
                  <Chip size="small" variant="outlined" label={`${(config.entry_counts?.active || 0) + (config.entry_counts?.pinned || 0)} learnings in use`} />
                  {!!config.entry_counts?.candidate && <Chip size="small" variant="outlined" label={`${config.entry_counts.candidate} waiting`} />}
                  {!!config.open_suggestions && (
                    <Chip size="small" color="secondary" label={`${config.open_suggestions} suggestions`} onClick={() => navigate(learningPath('function', functionId, 'suggestions'))} />
                  )}
                </>
              )}
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" startIcon={<TipsAndUpdatesIcon />} onClick={() => navigate(open)}>
                {on ? 'Open self-improvement' : 'Settings & first analysis'}
              </Button>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
